import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Award, Users, Clock, Shield, Star, Phone, MessageCircle, ChevronDown, MapPin, Zap, Heart, Quote, Mail, ArrowLeft } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work10.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }
`;

const HeroContent = styled.div`
  max-width: 1000px;
  width: 100%;
  padding: 2rem 1rem;
  position: relative;
  z-index: 2;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  margin-top: 80px; /* Account for fixed header */
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    padding: 1.5rem;
    margin-top: 70px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 60px;
    margin-bottom: 1rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 50px;
    margin-bottom: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    margin-top: 40px;
    margin-bottom: 0.75rem;
    border-radius: 10px;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(254, 206, 27, 0.4);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #FECE1B;
  box-shadow: 0 10px 30px rgba(254, 206, 27, 0.2);

  svg {
    color: #FECE1B;
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
  }

  @media (max-width: 375px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw, 5rem);
  margin-bottom: 1.5rem;
  font-weight: ${brandTheme.typography.fontWeight.extrabold};
  line-height: ${brandTheme.typography.lineHeight.tight};
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  font-family: ${brandTheme.typography.fontFamily.heading};
  filter: drop-shadow(0 0 20px rgba(254, 206, 27, 0.3));
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    letter-spacing: -0.01em;
  }

  @media (max-width: 375px) {
    font-size: clamp(1.5rem, 5vw, 2rem);
    margin-bottom: 0.75rem;
    line-height: 1.1;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 3vw, 1.6rem);
  margin-bottom: 3rem;
  opacity: 0.95;
  font-weight: 400;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.95);

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    font-size: clamp(1.1rem, 4vw, 1.4rem);
    line-height: 1.5;
  }

  @media (max-width: 375px) {
    margin-bottom: 1.5rem;
    font-size: clamp(1rem, 3.5vw, 1.2rem);
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 375px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const StyledButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  text-align: center;
  min-height: 60px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: #000000;
    box-shadow: 0 15px 30px rgba(254, 206, 27, 0.3), 0 0 0 1px rgba(254, 206, 27, 0.2);

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 20px 40px rgba(254, 206, 27, 0.4), 0 0 0 1px rgba(254, 206, 27, 0.3);
      color: #000000;
    }
  }

  &.secondary {
    background: rgba(0, 0, 0, 0.1);
    color: white;
    border: 2px solid rgba(254, 206, 27, 0.5);
    backdrop-filter: blur(20px);

    &:hover {
      background: rgba(254, 206, 27, 0.2);
      border-color: rgba(254, 206, 27, 0.8);
      transform: translateY(-2px);
    }
  }

  &.whatsapp {
    background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
    color: white;
    box-shadow: 0 15px 30px rgba(37, 211, 102, 0.3);

    &:hover {
      background: linear-gradient(135deg, #20c55a 0%, #16a34a 100%);
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 20px 40px rgba(37, 211, 102, 0.4);
    }
  }

  @media (max-width: 1024px) {
    padding: 1rem 1.5rem;
    font-size: 0.95rem;
    min-height: 55px;
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    min-height: 50px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
    min-height: 48px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.85rem;
    min-height: 44px;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
  }

  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.5rem 0.75rem;
  }

  @media (max-width: 375px) {
    bottom: 1rem;
    padding: 0.4rem 0.6rem;
    min-height: 44px;
    min-width: 44px;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 1024px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }

  @media (max-width: 375px) {
    padding: 0 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 1rem;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1.1rem, 2.5vw, 1.3rem);
  color: #64748b;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const StatCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e5e7eb;

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    color: #000000;
  }

  .number {
    font-size: 2.5rem;
    font-weight: bold;
    color: #FECE1B;
    margin-bottom: 0.5rem;
  }

  .label {
    color: #666;
    font-weight: 500;
  }
`;

const PartnershipSection = styled.section`
  background: white;
  padding: 5rem 0;
`;

const PartnershipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const PartnershipCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  align-items: center;
  background: #f8fafc;
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 2rem;
  }
`;

const PartnershipLogo = styled.div`
  text-align: center;
  
  img {
    max-width: 150px;
    height: auto;
    filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1));
    
    @media (max-width: 768px) {
      max-width: 120px;
    }
  }
`;

const PartnershipDetails = styled.div`
  h3 {
    color: #FECE1B;
    margin-bottom: 0.5rem;
    font-size: 1.375rem;
    font-weight: 700;
  }

  .partner-tagline {
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 1rem;
    font-style: italic;
  }

  p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-size: 0.95rem;

    a {
      color: #FECE1B;
      text-decoration: none;
      font-weight: 600;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const PartnershipCTA = styled.div`
  margin-top: 1.5rem;
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(254, 206, 27, 0.4);
  }
`;

const ValuesSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ValueCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  .icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    color: #000000;
  }

  h3 {
    color: #FECE1B;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
`;


const TestimonialsSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #FECE1B;

  .rating {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1rem;

    svg {
      color: #fbbf24;
      width: 20px;
      height: 20px;
    }
  }

  .reviewer {
    font-weight: 600;
    color: #FECE1B;
    margin-top: 1rem;
  }

  .location {
    color: #666;
    font-size: 0.9rem;
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  max-width: 800px;
  margin: 3rem auto 0;

  @media (max-width: 768px) {
    gap: 1rem;
    margin: 2rem auto 0;
  }
`;

const FAQItem = styled(motion.div)`
  background: #f8fafc;
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &.active {
    background: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .question {
    padding: 1.5rem;
    font-weight: 600;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #000000;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(254, 206, 27, 0.05);
    }

    svg {
      transition: transform 0.3s ease;
      color: #FECE1B;
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 1.5rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #64748b;
    line-height: 1.6;

    &.active {
      padding: 0 1.5rem 1.5rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem;
      font-size: 1rem;
    }

    .answer {
      padding: 0 1.25rem;
      
      &.active {
        padding: 0 1.25rem 1.25rem;
      }
    }
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  padding: 6rem 0;
  text-align: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #000000 0%, #333333 100%);
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const AboutPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const stats = [
    { number: '1000+', label: 'Happy Customers', icon: <Users size={24} /> },
    { number: '10+', label: 'Years Experience', icon: <Clock size={24} /> },
    { number: '5000+', label: 'Projects Completed', icon: <Award size={24} /> },
    { number: '5.0', label: 'Average Rating', icon: <Star size={24} /> }
  ];

  const values = [
    {
      icon: <Shield size={24} />,
      title: 'Quality First',
      description: 'We never compromise on quality. Every project is completed to the highest standards with premium materials and expert craftsmanship.'
    },
    {
      icon: <Clock size={24} />,
      title: 'Reliability',
      description: 'When we say we\'ll be there, we will be. We respect your time and property, delivering projects on schedule and within budget.'
    },
    {
      icon: <Users size={24} />,
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority. We listen to your needs, provide honest advice, and ensure every project exceeds expectations.'
    }
  ];


  const testimonials = [
    {
      rating: 5,
      review: "Exceptional service from start to finish. The team was professional, punctual, and the quality of work is outstanding. Our new driveway looks amazing!",
      reviewer: "David & Lisa Roberts",
      location: "Richmond, Surrey",
      projectType: "Driveway Installation"
    },
    {
      rating: 5,
      review: "We're so pleased with our resin pathway. The installation was quick and clean, and the team was very respectful of our property. Highly recommended!",
      reviewer: "Emma Williams",
      location: "St Albans, Hertfordshire",
      projectType: "Pathway Installation"
    },
    {
      rating: 5,
      review: "Great communication throughout the project. The team explained everything clearly and delivered exactly what was promised. Very happy with the result.",
      reviewer: "Michael & Sarah Johnson",
      location: "Hampstead, London",
      projectType: "Pool Deck Installation"
    }
  ];

  const faqs = [
    {
      question: 'How long has Resin Surfaces London been in business?',
      answer: 'Resin Surfaces London was founded in 2013, giving us over 10 years of experience in the resin bound surfacing industry. We\'ve completed thousands of successful projects across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire during this time.'
    },
    {
      question: 'What makes Resin Surfaces London different from other contractors?',
      answer: 'We\'re partnered with Vuba Surfaces, the UK\'s #1 resin bound manufacturer since 2009, ensuring we only use the highest quality materials. Our team has over 10 years of experience, we provide comprehensive warranties, and we\'re committed to customer satisfaction with a 5-star average rating.'
    },
    {
      question: 'Do you provide warranties on your work?',
      answer: 'Yes! We provide comprehensive warranties on all our installations, covering both materials and workmanship. Our standard warranty is 10+ years, giving you peace of mind that your investment is protected.'
    },
    {
      question: 'What areas do you cover?',
      answer: 'We serve all London boroughs, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire, and Oxfordshire. Our service area includes Richmond, Kingston, Croydon, St Albans, Watford, Hemel Hempstead, Canterbury, Maidstone, Chelmsford, Colchester, Aylesbury, High Wycombe, Reading, Windsor, and many more locations.'
    },
    {
      question: 'Are you fully insured and qualified?',
      answer: 'Yes, we are fully insured and qualified resin bound specialists. We have comprehensive public liability insurance, professional indemnity insurance, and all necessary qualifications. We\'re also members of relevant trade associations.'
    },
    {
      question: 'How do you ensure quality on every project?',
      answer: 'We use only premium materials from our partners Vuba Surfaces and GCL Products, employ skilled craftspeople with years of experience, and conduct thorough quality checks on every installation. Our commitment to quality is reflected in our 5-star average rating and thousands of satisfied customers.'
    },
    {
      question: 'What types of projects do you specialize in?',
      answer: 'We specialize in resin bound driveways, pathways, pool decks, and commercial surfacing. Our expertise covers residential and commercial projects, from small garden pathways to large commercial installations.'
    },
    {
      question: 'How can I get a quote for my project?',
      answer: 'Getting a quote is easy! You can call us on 07535 935 779, email info@resinsurfaceslondon.co.uk, or use our online contact form. We provide free, no-obligation quotes and site surveys for all projects.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>About Resin Surfaces London | Expert Resin Bound Surfacing Team | 10+ Years Experience | 1000+ Happy Customers | Partnered with Vuba Surfaces</title>
        <meta name="description" content="🏆 Meet the expert team at Resin Surfaces London! 10+ years experience, 1000+ happy customers, 5★ rated resin bound surfacing specialists. Partnered with Vuba Surfaces (UK's #1 manufacturer) & GCL Products. Expert installation across London, Surrey & Hertfordshire. Learn about our commitment to quality!" />
        <meta name="keywords" content="about resin surfaces London, resin bound specialists London, resin surfacing company London, experienced resin installers London, resin driveway experts London, resin bound team London, resin surfacing professionals London, about resin driveway company London, resin bound specialists team London, resin surfacing experts about London, experienced resin contractors London, professional resin installers London, resin bound company history London, resin surfacing team London, about resin driveway specialists London, resin bound installation experts London, resin surfacing company story London, resin driveway professionals about London, resin bound team experience London, resin surfacing company values London, about resin specialists London, resin bound company mission London, resin driveway team expertise London, resin surfacing company background London, about resin bound experts London, resin driveway company team London, resin bound specialists experience London, resin surfacing professionals about London, resin driveway company values London, about resin installation experts London, resin driveways Westminster, resin bound surfacing Camden, resin driveways Islington, resin surfacing Hackney, resin bound Tower Hamlets, resin driveways Greenwich, resin surfacing Lewisham, resin bound Southwark, resin driveways Lambeth, resin surfacing Wandsworth, resin bound Hammersmith, resin driveways Kensington, resin surfacing Chelsea, resin bound Fulham, resin driveways Richmond, resin surfacing Kingston, resin bound Merton, resin driveways Sutton, resin surfacing Croydon, resin bound Bromley, resin driveways Bexley, resin surfacing Havering, resin bound Redbridge, resin driveways Waltham Forest, resin surfacing Enfield, resin bound Barnet, resin driveways Harrow, resin surfacing Hillingdon, resin bound Ealing, resin driveways Hounslow, resin surfacing Brent, resin bound Harrow, resin driveways Haringey, resin surfacing Newham, resin bound Barking, resin driveways Dagenham" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Resin Surfaces London" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/about-us/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/about-us/" />
        <meta property="og:title" content="🏆 About Resin Surfaces London | Expert Team | 10+ Years Experience | 1000+ Happy Customers" />
        <meta property="og:description" content="Meet the expert team at Resin Surfaces London! 10+ years experience, 1000+ happy customers, 5★ rated resin bound surfacing specialists. Partnered with Vuba Surfaces & GCL Products." />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Resin Surfaces London" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.resinsurfaceslondon.co.uk/about-us/" />
        <meta name="twitter:title" content="🏆 About Resin Surfaces London | Expert Team | 10+ Years Experience" />
        <meta name="twitter:description" content="Meet the expert team at Resin Surfaces London! 10+ years experience, 1000+ happy customers, 5★ rated resin bound surfacing specialists." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg" />
        <meta name="twitter:site" content="@ResinSurfacesLdn" />
        <meta name="twitter:creator" content="@ResinSurfacesLdn" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="GB-LND" />
        <meta name="geo.placename" content="London" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="1 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="target" content="all" />
        <meta name="HandheldFriendly" content="True" />
        <meta name="MobileOptimized" content="320" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": "https://www.resinsurfaceslondon.co.uk/about-us/#aboutpage",
            "name": "About Resin Surfaces London - Expert Resin Bound Specialists",
            "description": "Learn about Resin Surfaces London - expert resin bound surfacing specialists with 10+ years experience, 1000+ happy customers, and partnerships with industry leaders.",
            "url": "https://www.resinsurfaceslondon.co.uk/about-us/",
            "mainEntity": {
              "@type": "Organization",
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization",
              "name": "ZAN RESIN SURFACES LTD",
              "alternateName": "Resin Surfaces London",
              "description": "🏆 #1 Premium resin bound surfacing specialists for driveways, pathways and pool decks across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire. 1000+ happy customers, 10+ year warranty, 5★ reviews.",
              "telephone": "+44 7535 935779",
              "email": "info@resinsurfaceslondon.co.uk",
              "vatID": "16698534",
              "logo": "https://www.resinsurfaceslondon.co.uk/logo.png",
              "image": "https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work10.jpeg",
              "foundingDate": "2013",
              "numberOfEmployees": "10-20",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Broadfield Broadhurst Gardens",
                "addressLocality": "London",
                "addressRegion": "London",
                "postalCode": "NW6 3BN",
                "addressCountry": "GB"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 51.5074,
                "longitude": -0.1278
              },
              "areaServed": [
                {"@type": "City", "name": "London"},
                {"@type": "City", "name": "Surrey"},
                {"@type": "City", "name": "Hertfordshire"},
                {"@type": "City", "name": "Buckinghamshire"},
                {"@type": "City", "name": "Kent"},
                {"@type": "City", "name": "Essex"},
                {"@type": "City", "name": "Berkshire"},
                {"@type": "City", "name": "Oxfordshire"}
              ],
              "serviceType": [
                "Resin Bound Surfacing", 
                "Driveway Installation", 
                "Pathway Installation", 
                "Pool Deck Installation",
                "Permeable Surfacing",
                "SUDS Compliant Surfacing",
                "Anti-slip Surfacing",
                "Commercial Resin Surfacing",
                "Residential Resin Driveways"
              ],
              "priceRange": "££",
              "paymentAccepted": ["Cash", "Card", "Bank Transfer"],
              "currenciesAccepted": "GBP",
              "openingHours": "Mo-Fr 08:00-18:00,Sa 09:00-16:00",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5.0",
                "reviewCount": "127",
                "bestRating": "5",
                "worstRating": "1"
              },
              "review": [
                {
                  "@type": "Review",
                  "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": "5",
                    "bestRating": "5"
                  },
                  "author": {
                    "@type": "Person",
                    "name": "David & Lisa Roberts"
                  },
                  "reviewBody": "Exceptional service from start to finish. The team was professional, punctual, and the quality of work is outstanding. Our new driveway looks amazing!"
                }
              ],
              "sameAs": [
                "https://www.facebook.com/resinsurfaceslondon",
                "https://www.instagram.com/resinsurfaceslondon",
                "https://www.linkedin.com/company/resin-surfaces-london"
              ]
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.resinsurfaceslondon.co.uk/about-us/#webpage",
            "url": "https://www.resinsurfaceslondon.co.uk/about-us/",
            "name": "About Resin Surfaces London - Expert Resin Bound Specialists",
            "description": "Learn about Resin Surfaces London - expert resin bound surfacing specialists with 10+ years experience and 1000+ happy customers.",
            "isPartOf": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#website"
            },
            "about": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            },
            "publisher": {
              "@id": "https://www.resinsurfaceslondon.co.uk/#organization"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How long has Resin Surfaces London been in business?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Resin Surfaces London was founded in 2013, giving us over 10 years of experience in the resin bound surfacing industry. We've completed thousands of successful projects across London, Surrey, and Hertfordshire during this time."
                }
              },
              {
                "@type": "Question",
                "name": "What makes Resin Surfaces London different from other contractors?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We're partnered with Vuba Surfaces, the UK's #1 resin bound manufacturer since 2009, ensuring we only use the highest quality materials. Our team has over 10 years of experience, we provide comprehensive warranties, and we're committed to customer satisfaction with a 5-star average rating."
                }
              },
              {
                "@type": "Question",
                "name": "Do you provide warranties on your work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! We provide comprehensive warranties on all our installations, covering both materials and workmanship. Our standard warranty is 10+ years, giving you peace of mind that your investment is protected."
                }
              },
              {
                "@type": "Question",
                "name": "What areas do you cover?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We serve all London boroughs, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire, and Oxfordshire. Our service area includes Richmond, Kingston, Croydon, St Albans, Watford, Hemel Hempstead, and many more locations."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="About Resin Surfaces London"
        subtitle="Your trusted experts in premium resin bound surfacing across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire & Berkshire. Partnered with industry leaders Vuba Surfaces and GCL Products for the highest quality installations."
        badge="10+ Years Experience • 1000+ Happy Customers"
        backgroundImage="/images/resin-surfaces-bound-work10.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <TwoColumnGrid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Resin Surfaces London team" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Our Story</h2>
              <p>
                Resin Surfaces London was founded in 2013 with a simple mission: to provide the highest 
                quality resin bound surfacing solutions to homeowners and businesses across London, Surrey, 
                and Hertfordshire. What started as a small family business has grown into one of the region's 
                most trusted resin surfacing specialists.
              </p>
              <p>
                Over the past 10+ years, we've completed thousands of successful projects, from residential 
                driveways to commercial pathways and pool surrounds. Our commitment to quality, reliability, 
                and customer satisfaction has earned us a reputation for excellence in the industry.
              </p>
              <p>
                We believe that every outdoor space deserves to be both beautiful and functional. That's why 
                we only use premium materials, employ skilled craftspeople, and stand behind our work with 
                comprehensive warranties and ongoing support.
              </p>
            </motion.div>
          </TwoColumnGrid>

          <SectionTitle>Our Track Record</SectionTitle>
          <SectionSubtitle>
            Our success is measured by the satisfaction of our customers and the quality of our work
          </SectionSubtitle>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{stat.icon}</div>
                <div className="number">{stat.number}</div>
                <div className="label">{stat.label}</div>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </Section>

      <PartnershipSection>
        <Container>
          <SectionTitle>Proud Partnerships with Industry Leaders</SectionTitle>
          <PartnershipsGrid>
            <PartnershipCard>
              <PartnershipLogo>
                <img src="/vuba_resin_logo_uk.png" alt="Vuba Surfaces - UK's #1 Resin Bound Surfacing Manufacturer" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>Vuba Surfaces</h3>
                <p className="partner-tagline">UK's #1 Resin Bound Manufacturer since 2009</p>
                <p>
                  We're proud to partner with <a href="https://www.vubasurfaces.co.uk/" target="_blank" rel="noopener noreferrer">Vuba Surfaces</a>, 
                  the UK's #1 resin bound surfacing manufacturer since 2009. This strategic partnership ensures that every project 
                  we complete uses only the highest quality, BBA-certified materials.
                </p>
                <p>
                  Vuba Surfaces has been at the forefront of resin bound innovation for over 15 years, providing award-winning 
                  solutions that meet the highest industry standards. Their commitment to excellence aligns perfectly with our 
                  values of quality, reliability, and customer satisfaction.
                </p>
                <PartnershipCTA>
                  <a 
                    href="https://www.vubasurfaces.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About Vuba <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>

            <PartnershipCard>
              <PartnershipLogo>
                <img src="/gcl_products_logo_uk.png" alt="GCL Products - Groundworks, Construction & Landscaping" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>GCL Products</h3>
                <p className="partner-tagline">Groundworks, Construction & Landscaping</p>
                <p>
                  Our partnership with <a href="https://www.gclproducts.co.uk/" target="_blank" rel="noopener noreferrer">GCL Products</a> 
                  provides access to a comprehensive range of groundworks, construction, and landscaping products. 
                  GCL Products Ltd delivers high-quality materials across the UK, ensuring reliable supply and consistent quality.
                </p>
                <p>
                  This partnership enables us to source premium materials for all aspects of our projects, from groundworks 
                  to finishing touches, ensuring every installation meets the highest standards of quality and durability.
                </p>
                <PartnershipCTA>
                  <a 
                    href="https://www.gclproducts.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About GCL <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>
          </PartnershipsGrid>
        </Container>
      </PartnershipSection>

      <ValuesSection>
        <Container>
          <SectionTitle>Our Values</SectionTitle>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </ValueCard>
            ))}
          </ValuesGrid>
        </Container>
      </ValuesSection>


      <TestimonialsSection>
        <Container>
          <SectionTitle>What Our Customers Say</SectionTitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="currentColor" />
                  ))}
                </div>
                <p>"{testimonial.review}"</p>
                <div className="reviewer">{testimonial.reviewer}</div>
                <div className="location">{testimonial.location}</div>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </Container>
      </TestimonialsSection>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Our Company</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about Resin Surfaces London, our team, experience, and services
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: '#000000' }}>Ready to Work with the Experts?</SectionTitle>
          <p style={{ 
            fontSize: '1.2rem', 
            marginBottom: '2rem', 
            opacity: 0.9, 
            color: '#000000',
            maxWidth: '600px',
            margin: '0 auto 2rem auto'
          }}>
            Join thousands of satisfied customers who trust Resin Surfaces London for their outdoor surfacing needs
          </p>
          <CTAButtons>
            <StyledButton href="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton href="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
            <StyledButton href="tel:+447535935779" className="secondary">
              <Phone size={20} /> Call Now
            </StyledButton>
          </CTAButtons>
        </Container>
      </CTASection>
    </>
  );
};

export default AboutPage;
